import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, FileText, FileSpreadsheet, Paperclip, Minus, Plus, Loader2 } from 'lucide-react';

// Preglednik priloga preko cijelog ekrana — ostaje u aplikaciji.
// Slika i PDF (pdf.js, jer mobilni preglednici PDF u iframeu ne prikazuju) se prikazuju ovdje,
// Word/Excel dobiju karticu s „Preuzmi i otvori”. Gumb „natrag” na mobitelu zatvara preglednik (history entry).

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const clamp = (v) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, v));

export function attachmentKind(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png'].includes(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  if (['doc', 'docx'].includes(ext)) return 'word';
  if (['xls', 'xlsx'].includes(ext)) return 'excel';
  return 'file';
}

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1).replace('.', ',') + ' MB';
}

// Jedna PDF stranica — crta se tek kad uđe u vidno polje, ponovno nakon promjene zuma (s odgodom)
function PdfPage({ doc, index, size, cssWidth, root }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const cssHeight = cssWidth * (size.h / size.w);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { root, rootMargin: '600px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [root]);

  useEffect(() => {
    if (!visible || !cssWidth) return;
    let task = null;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const page = await doc.getPage(index + 1);
        if (cancelled) return;
        const pr = Math.min(window.devicePixelRatio || 1, 2);
        const px = Math.min(cssWidth * pr, 4000);
        const viewport = page.getViewport({ scale: px / size.w });
        const canvas = canvasRef.current;
        if (!canvas) return;
        const off = document.createElement('canvas');
        off.width = Math.floor(viewport.width);
        off.height = Math.floor(viewport.height);
        task = page.render({ canvasContext: off.getContext('2d'), viewport });
        await task.promise;
        if (cancelled) return;
        // Prebaci tek gotov crtež — nema bijelog treptaja pri zumiranju
        canvas.width = off.width;
        canvas.height = off.height;
        canvas.getContext('2d').drawImage(off, 0, 0);
      } catch { /* otkazano ili neispravna stranica */ }
    }, 180);
    return () => { cancelled = true; clearTimeout(t); if (task) task.cancel(); };
  }, [visible, cssWidth, doc, index, size.w]);

  return (
    <div ref={wrapRef} className="attv-page" style={{ width: cssWidth, height: cssHeight }}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
}

function PdfView({ url, scale, baseWidth, scrollEl, onPages }) {
  const [doc, setDoc] = useState(null);
  const [sizes, setSizes] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let loadingTask = null;
    let loaded = null;
    (async () => {
      const pdfjs = await import('pdfjs-dist/legacy/build/pdf');
      pdfjs.GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.js';
      loadingTask = pdfjs.getDocument({ url, isEvalSupported: false, standardFontDataUrl: '/pdfjs/standard_fonts/' });
      loaded = await loadingTask.promise;
      const s = [];
      for (let i = 1; i <= loaded.numPages; i++) {
        const v = (await loaded.getPage(i)).getViewport({ scale: 1 });
        s.push({ w: v.width, h: v.height });
        if (cancelled) return;
      }
      if (cancelled) return;
      setSizes(s);
      setDoc(loaded);
      onPages && onPages(loaded.numPages);
    })().catch(() => { if (!cancelled) setError(true); });
    return () => {
      cancelled = true;
      if (loadingTask) loadingTask.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  if (error) return <div className="attv-msg">PDF se ne može prikazati. Preuzmi ga gumbom gore.</div>;
  if (!doc || !baseWidth) return <div className="attv-msg"><Loader2 size={26} className="attv-spin" /></div>;
  return (
    <div className="attv-pages">
      {sizes.map((sz, i) => (
        <PdfPage key={i} doc={doc} index={i} size={sz} cssWidth={Math.round(baseWidth * scale)} root={scrollEl} />
      ))}
    </div>
  );
}

export default function AttachmentViewer({ attachment, onClose }) {
  const url = attachment.url || `/task-attachments/${attachment.filename}`;   // url: npr. fotografije intervencija (/uploads/)
  const ext = (attachment.filename || '').includes('.') ? attachment.filename.split('.').pop().toLowerCase() : '';
  const dlName = ext && !(attachment.original_name || '').toLowerCase().endsWith('.' + ext)
    ? `${attachment.original_name || 'prilog'}.${ext}` : (attachment.original_name || attachment.filename);
  // Vrsta po nastavku datoteke na serveru — korisnički naziv priloga često nema nastavak
  const kind = attachmentKind(attachment.filename || attachment.original_name);
  const zoomable = kind === 'image' || kind === 'pdf';
  const [scale, setScale] = useState(1);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [natural, setNatural] = useState(null);     // prirodna veličina slike
  const [pages, setPages] = useState(0);
  const [scrollEl, setScrollEl] = useState(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const prevScale = useRef(1);
  const pinch = useRef(null);
  const lastTap = useRef(0);

  // „Natrag” zatvara preglednik: vlastiti zapis u povijesti; X ide kroz history.back() da zapis ne ostane
  useEffect(() => {
    let popped = false;
    window.history.pushState({ ...(window.history.state || {}), attViewer: true }, '');
    const onPop = () => { popped = true; onCloseRef.current(); };
    const onKey = (e) => { if (e.key === 'Escape') window.history.back(); };
    window.addEventListener('popstate', onPop);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('popstate', onPop);
      document.removeEventListener('keydown', onKey);
      if (!popped && window.history.state && window.history.state.attViewer) window.history.back();
    };
  }, []);
  const close = () => window.history.back();

  // Veličina područja za prikaz
  useEffect(() => {
    if (!scrollEl) return;
    const measure = () => setBox({ w: scrollEl.clientWidth, h: scrollEl.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(scrollEl);
    return () => ro.disconnect();
  }, [scrollEl]);

  // Zum drži središte pogleda na mjestu
  useLayoutEffect(() => {
    const el = scrollEl;
    const prev = prevScale.current;
    prevScale.current = scale;
    if (!el || prev === scale) return;
    const r = scale / prev;
    const cx = el.scrollLeft + el.clientWidth / 2;
    const cy = el.scrollTop + el.clientHeight / 2;
    el.scrollLeft = cx * r - el.clientWidth / 2;
    el.scrollTop = cy * r - el.clientHeight / 2;
  }, [scale, scrollEl]);

  // Pinch (dva prsta) i dvostruki dodir — preglednikov zum je u aplikaciji isključen (maximum-scale=1)
  useEffect(() => {
    const el = scrollEl;
    if (!el || !zoomable) return;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const onStart = (e) => {
      if (e.touches.length === 2) {
        pinch.current = { d: dist(e.touches), s: prevScale.current };
      } else if (e.touches.length === 1) {
        const now = Date.now();
        if (now - lastTap.current < 280) {
          e.preventDefault();
          setScale(s => (s > 1.05 ? 1 : 2.5));
          lastTap.current = 0;
        } else {
          lastTap.current = now;
        }
      }
    };
    const onMove = (e) => {
      if (!pinch.current || e.touches.length !== 2) return;
      e.preventDefault();
      setScale(clamp(pinch.current.s * (dist(e.touches) / pinch.current.d)));
    };
    const onEnd = (e) => { if (e.touches.length < 2) pinch.current = null; };
    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [scrollEl, zoomable]);

  const zoomBy = (f) => setScale(s => clamp(Math.round(s * f * 4) / 4));

  // Slika: širina „stane u ekran” × zum
  let imgW = 0;
  if (kind === 'image' && natural && box.w) {
    const pad = 24;
    imgW = Math.min(natural.w, box.w - pad, (box.h - pad) * (natural.w / natural.h)) * scale;
  }
  const pdfBase = box.w ? Math.min(box.w - 16, 900) : 0;

  const sub = [formatSize(attachment.file_size), pages ? `${pages} ${pages === 1 ? 'stranica' : pages < 5 ? 'stranice' : 'stranica'}` : ''].filter(Boolean).join(' · ');

  return createPortal(
    <div className="attv" role="dialog" aria-modal="true" aria-label={attachment.original_name}>
      <div className="attv-bar">
        <div className="attv-title">
          <div className="attv-name">{attachment.original_name}</div>
          {sub && <div className="attv-sub">{sub}</div>}
        </div>
        {zoomable && (
          <a className="attv-ib" href={url} download={dlName} title="Preuzmi">
            <Download size={19} />
          </a>
        )}
        <button type="button" className="attv-ib attv-x" onClick={close} title="Zatvori">
          <X size={22} />
        </button>
      </div>

      <div ref={setScrollEl} className={`attv-body attv-${kind}`}>
        {kind === 'image' && (
          <div className="attv-center">
            <img
              src={url}
              alt={attachment.original_name}
              draggable={false}
              onLoad={e => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
              style={imgW ? { width: imgW } : { maxWidth: '100%', maxHeight: '100%', visibility: natural ? 'visible' : 'hidden' }}
            />
          </div>
        )}
        {kind === 'pdf' && (
          <PdfView url={url} scale={scale} baseWidth={pdfBase} scrollEl={scrollEl} onPages={setPages} />
        )}
        {!zoomable && (
          <div className="attv-file">
            <div className="attv-file-ico" data-kind={kind}>
              {kind === 'word' ? <FileText size={34} /> : kind === 'excel' ? <FileSpreadsheet size={34} /> : <Paperclip size={32} />}
            </div>
            <div className="attv-file-name">{attachment.original_name}</div>
            <div className="attv-file-sub">{formatSize(attachment.file_size)}</div>
            <p className="attv-file-note">
              {kind === 'word'
                ? 'Word dokument se ne može prikazati ovdje. Preuzmi ga i otvori u Wordu na uređaju, pa se vrati ovamo.'
                : kind === 'excel'
                  ? 'Excel tablica se ne može prikazati ovdje. Preuzmi je i otvori u Excelu na uređaju, pa se vrati ovamo.'
                  : 'Ova datoteka se ne može prikazati ovdje. Preuzmi je i otvori na uređaju, pa se vrati ovamo.'}
            </p>
            <a className="attv-file-btn" href={url} download={dlName}>
              <Download size={18} /> Preuzmi i otvori
            </a>
          </div>
        )}
      </div>

      {zoomable && (
        <div className="attv-zoom">
          <button type="button" onClick={() => zoomBy(1 / 1.5)} disabled={scale <= MIN_SCALE} title="Umanji"><Minus size={18} /></button>
          <button type="button" className="attv-zoom-v" onClick={() => setScale(1)} title="Vrati na 100 %">{Math.round(scale * 100)} %</button>
          <button type="button" onClick={() => zoomBy(1.5)} disabled={scale >= MAX_SCALE} title="Povećaj"><Plus size={18} /></button>
        </div>
      )}
    </div>,
    document.body
  );
}
